import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUS_COLOR = {
  present: "bg-emerald-500",
  absent: "bg-destructive",
  late: "bg-amber-500",
  excused: "bg-sky-500",
  null: "bg-secondary",
};

// Given N days, return them grouped by ISO week (arrays of 7 with weekday index)
function buildWeekGrid(grid) {
  if (!grid.length) return [];
  // Sort by date
  const sorted = [...grid].sort((a, b) => a.date.localeCompare(b.date));
  const weeks = [];
  let currentWeek = new Array(7).fill(null);
  sorted.forEach((cell) => {
    const d = new Date(cell.date + "T00:00:00");
    const day = (d.getDay() + 6) % 7; // Monday=0 ... Sunday=6
    if (day === 0 && currentWeek.some(Boolean)) {
      weeks.push(currentWeek);
      currentWeek = new Array(7).fill(null);
    }
    currentWeek[day] = cell;
  });
  if (currentWeek.some(Boolean)) weeks.push(currentWeek);
  return weeks;
}

export default function AttendanceHeatmap({ studentId, days = 91 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!studentId) return;
    api.get(`/attendance/heatmap`, { params: { student_id: studentId, days } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [studentId, days]);

  const weeks = useMemo(() => data ? buildWeekGrid(data.grid) : [], [data]);
  const dayLabels = ["M", "T", "W", "T", "F", "S", "S"];

  if (!data) return <div className="text-sm text-muted-foreground">Loading heatmap...</div>;

  return (
    <div data-testid="attendance-heatmap" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Last {days} days</div>
          <div className="text-sm text-muted-foreground font-mono">{data.start} → {data.end}</div>
        </div>
        <div className="flex gap-4 text-xs">
          {["present", "absent", "late", "excused"].map((k) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className={cn("w-3 h-3 rounded-sm", STATUS_COLOR[k])} />
              <span className="capitalize text-muted-foreground">{k}</span>
              <span className="font-mono font-semibold">{data.summary[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto scrollbar-thin py-2">
        <div className="flex flex-col gap-1 shrink-0 pt-4">
          {dayLabels.map((d, i) => (
            <div key={i} className="w-3 h-3 text-[9px] text-muted-foreground font-mono flex items-center">{i % 2 === 0 ? d : ""}</div>
          ))}
        </div>
        {weeks.map((w, wi) => (
          <div key={wi} className="flex flex-col gap-1 shrink-0">
            {w.map((cell, di) => (
              <div
                key={di}
                title={cell ? `${cell.date} — ${cell.status || "no record"}` : ""}
                className={cn(
                  "w-3 h-3 rounded-sm transition-transform hover:scale-125 hover:ring-2 hover:ring-primary/30",
                  cell ? STATUS_COLOR[cell.status] || "bg-secondary/70 border border-border" : "bg-transparent",
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
